import type { PlanGlyphReport } from '@models/map/dxfTypes';

/**
 * Règles de saisie du dictionnaire de glyphes, côté front.
 *
 * Elles DOIVENT rester alignées sur `App\Service\Plan\Glyph\GlyphDictionary` : le serveur
 * normalise puis valide chaque entrée, et **ignore silencieusement** celles qu'il refuse —
 * une frappe ne doit pas faire perdre tout un étiquetage. Sans contrôle ici, l'opérateur
 * saisirait « ø » et ne verrait jamais que sa saisie a été jetée.
 */

/** Caractères admis : format d'emplacement de salon (`GlyphDictionary::ALLOWED_CHARACTER`). */
const ALLOWED_CHARACTER = /^[0-9A-Z.\- ]$/;

/** Normalisation appliquée par le serveur avant validation : trim + majuscule. */
export function normalizeGlyphCharacter(value: string): string {
	return value.trim().toUpperCase();
}

/**
 * La saisie sera-t-elle RETENUE par le serveur ?
 *
 * Une chaîne vide est valide au sens « rien à enregistrer » : elle n'est simplement pas
 * envoyée. C'est ce qui permet d'étiqueter en plusieurs fois.
 */
export function isValidGlyphCharacter(value: string): boolean {
	const normalized = normalizeGlyphCharacter(value);

	return '' === normalized || ALLOWED_CHARACTER.test(normalized);
}

/**
 * Entrées prêtes à être envoyées : normalisées, vides écartées, invalides écartées.
 *
 * @param values signature => saisie brute de l'opérateur
 *
 * @return signature => caractère
 */
export function toGlyphEntries(values: Record<string, string>): Record<string, string> {
	const entries: Record<string, string> = {};

	for (const [signature, raw] of Object.entries(values)) {
		const character = normalizeGlyphCharacter(raw);
		if ('' !== character && ALLOWED_CHARACTER.test(character)) {
			entries[signature] = character;
		}
	}

	return entries;
}

/**
 * Ce plan a-t-il besoin d'un étiquetage ?
 *
 * Non pour un DXF (texte en clair, l'opération lui est refusée) ni pour un PDF à couche
 * texte — le serveur renvoie alors un rapport VIDE, ce qui n'est pas une erreur mais la
 * bascule normale de `docs/ETUDE_SVG_PDF.md` §5.1. On ne montre l'étape que s'il reste
 * réellement des formes à nommer.
 */
export function needsLabelling(report: PlanGlyphReport | null): boolean {
	return !!report && report.unresolved.length > 0;
}

/** Part des libellés entièrement lisibles, en pourcentage entier — le chiffre à afficher. */
export function labelCoveragePercent(report: PlanGlyphReport): number {
	return Math.round(report.labelCoverage * 100);
}
